import React, { useEffect, useState } from "react";
import { Plus, Edit, Trash2, Search } from "lucide-react";
import { toast } from "react-toastify";
import api from "../utils/api";

const PlanManagement = () => {
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingPlan, setEditingPlan] = useState(null);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");

  const [form, setForm] = useState({
    title: "",
    description: "",
    amount: "",
    duration_days: 30,
    highlighting_feature: "",
    plan_type: "public",
    is_active: true,
  });

  const [features, setFeatures] = useState([""]);

  /* ================= FETCH PLANS ================= */
  const fetchPlans = async () => {
    try {
      setLoading(true);
      const res = await api.get(`plans`);
      setPlans(res.data.data || []);
    } catch (err) {
      console.error(err);
      toast.error("Failed to load plans");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPlans();
  }, []);

  /* ================= CREATE / UPDATE ================= */
  const savePlan = async (e) => {
    e.preventDefault();

    // Validation
    if (form.amount === "" || Number(form.amount) < 0) {
      toast.error("Amount is required (enter 0 for a free plan)");
      return;
    }
    
    if (Number(form.duration_days) <= 0) {
      toast.error("Duration must be greater than 0 days");
      return;
    }

    if (!form.title.trim()) {
      toast.error("Plan title is required");
      return;
    }

    // Prepare data
    const dataToSend = {
      ...form,
      amount: Number(form.amount),
      duration_days: Number(form.duration_days),
      features: features.filter(f => f.trim() !== ""),
    };

    try {
      if (editingPlan) {
        await api.patch(`plans/${editingPlan._id}`, dataToSend);
        toast.success("Plan updated successfully!");
      } else {
        await api.post(`plans`, dataToSend);
        toast.success("Plan created successfully!");
      }

      fetchPlans();
      closeModal();
    } catch (err) {
      console.error(err.response?.data || err);
      toast.error(err.response?.data?.message || "Failed to save plan");
    }
  };

  /* ================= ACTIVATE / DEACTIVATE ================= */
  const togglePlanStatus = (plan) => {
    const isDeactivating = plan.is_active;

    toast.info(
      <div>
        <p className="font-medium">
          {isDeactivating
            ? "Are you sure you want to deactivate this plan?"
            : "Are you sure you want to activate this plan?"}
        </p>

        <div className="flex justify-center mt-3 gap-3">
          <button
            className={`px-4 py-1.5 rounded text-white ${
              isDeactivating ? "bg-red-500 hover:bg-red-600" : "bg-green-500 hover:bg-green-600"
            } transition-colors`}
            onClick={async () => {
              try {
                await api.patch(
                  `plans/${plan._id}/deactivate`,
                  { is_active: !plan.is_active },
                );

                toast.dismiss();
                toast.success(
                  isDeactivating
                    ? "Plan deactivated successfully!"
                    : "Plan activated successfully!",
                );
                fetchPlans();
              } catch (error) {
                toast.dismiss();
                const message = error.response?.data?.message || "Failed to update plan status!";
                toast.error(message);
              }
            }}
          >
            Yes
          </button>

          <button
            className="bg-gray-300 text-black px-4 py-1.5 rounded hover:bg-gray-400 transition-colors"
            onClick={() => toast.dismiss()}
          >
            No
          </button>
        </div>
      </div>,
      {
        position: "top-center",
        autoClose: false,
        closeOnClick: false,
        closeButton: true,
        draggable: false,
      },
    );
  };

  /* ================= DELETE (SOFT) ================= */
  const deletePlan = (plan) => {
    toast.info(
      <div>
        <p className="font-medium text-red-600">
          Delete Plan: {plan.title}
        </p>
        <p className="text-sm text-gray-600 mt-1">
          This action cannot be undone. All existing subscriptions will be affected.
        </p>

        <div className="flex justify-center mt-3 gap-3">
          <button
            className="bg-red-600 text-white px-4 py-1.5 rounded hover:bg-red-700 transition-colors"
            onClick={async () => {
              try {
                await api.delete(`plans/${plan._id}`);

                toast.dismiss();
                toast.success("Plan deleted successfully!");
                fetchPlans();
              } catch (error) {
                toast.dismiss();
                toast.error(
                  error.response?.data?.message || "Failed to delete plan!",
                );
              }
            }}
          >
            Delete
          </button>

          <button
            className="bg-gray-300 text-black px-4 py-1.5 rounded hover:bg-gray-400 transition-colors"
            onClick={() => toast.dismiss()}
          >
            Cancel
          </button>
        </div>
      </div>,
      {
        position: "top-center",
        autoClose: false,
        closeOnClick: false,
        closeButton: true,
        draggable: false,
      },
    );
  };

  /* ================= UI HELPERS ================= */
  const openEdit = (plan) => {
    setEditingPlan(plan);
    setForm({
      title: plan.title,
      description: plan.description,
      amount: plan.amount,
      duration_days: plan.duration_days,
      highlighting_feature: plan.highlighting_feature,
      plan_type: plan.plan_type || "public",
      is_active: plan.is_active,
    });
    setFeatures(plan.features?.length > 0 ? plan.features : [""]);
    setShowModal(true);
  };

  const closeModal = () => {
    setEditingPlan(null);
    setShowModal(false);
    setForm({
      title: "",
      description: "",
      amount: "",
      duration_days: 30,
      highlighting_feature: "",
      plan_type: "public",
      is_active: true,
    });
    setFeatures([""]);
  };

  // Feature management
  const addFeature = () => {
    setFeatures([...features, ""]);
  };

  const updateFeature = (index, value) => {
    const newFeatures = [...features];
    newFeatures[index] = value;
    setFeatures(newFeatures);
  };

  const removeFeature = (index) => {
    if (features.length > 1) {
      const newFeatures = features.filter((_, i) => i !== index);
      setFeatures(newFeatures);
    }
  };

  // Filter plans
  const filteredPlans = plans.filter((plan) => {
    const matchesSearch =
      plan.title.toLowerCase().includes(search.toLowerCase()) ||
      plan.description.toLowerCase().includes(search.toLowerCase()) ||
      (plan.plan_type && plan.plan_type.toLowerCase().includes(search.toLowerCase()));

    const matchesStatus =
      statusFilter === "all" ||
      (statusFilter === "active" && plan.is_active) ||
      (statusFilter === "inactive" && !plan.is_active);

    return matchesSearch && matchesStatus;
  });

  /* ================= RENDER ================= */
  return (
    <div className="p-6">
      {/* Header with Controls */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-6">
        {/* Left: Title */}
        <div className="text-center md:text-left">
          <h2 className="text-2xl font-bold text-gray-800">Subscription Plans</h2>
          <p className="text-gray-600 text-sm">Manage pricing plans for your platform</p>
        </div>

        {/* Right: Controls */}
        <div className="flex flex-col sm:flex-row flex-wrap gap-3 w-full md:w-auto">
          {/* Search */}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
            <input
              type="text"
              placeholder="Search plans..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="border border-gray-300 pl-9 pr-4 py-2 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 w-full sm:w-64"
            />
          </div>

          {/* Status Filter */}
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="border border-gray-300 px-4 py-2 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 w-full sm:w-40"
          >
            <option value="all">All Plans</option>
            <option value="active">Active Only</option>
            <option value="inactive">Inactive Only</option>
          </select>

          {/* Create Button */}
          <button
            onClick={() => setShowModal(true)}
            className="flex items-center justify-center gap-2 bg-blue-600 text-white px-4 py-2.5 rounded-lg hover:bg-blue-700 transition-colors w-full sm:w-auto"
          >
            <Plus size={18} />
            Create Plan
          </button>
        </div>
      </div>

      {/* Plans Grid */}
      {loading ? (
        // Loading Skeletons
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {[...Array(6)].map((_, i) => (
            <div key={i} className="border rounded-xl p-2 bg-white animate-pulse">
              <div className="flex justify-between items-start mb-4">
                <div className="h-6 bg-gray-200 rounded w-3/4"></div>
                <div className="h-6 bg-gray-200 rounded w-16"></div>
              </div>
              <div className="h-8 bg-gray-200 rounded w-2/3 mb-4"></div>
              <div className="h-4 bg-gray-200 rounded w-full mb-2"></div>
              <div className="h-4 bg-gray-200 rounded w-4/5 mb-2"></div>
              <div className="h-4 bg-gray-200 rounded w-3/4 mb-4"></div>
              <div className="h-4 bg-gray-200 rounded w-1/2 mb-4"></div>
              <div className="h-10 bg-gray-200 rounded mt-4"></div>
            </div>
          ))}
        </div>
      ) : filteredPlans.length === 0 ? (
        // Empty State
        <div className="text-center py-12 bg-gray-50 rounded-xl border border-gray-200">
          <div className="text-gray-400 mb-3">
            <svg className="w-16 h-16 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
          </div>
          <h3 className="text-lg font-medium text-gray-700 mb-2">No plans found</h3>
          <p className="text-gray-500 mb-4">
            {search || statusFilter !== "all" 
              ? "Try adjusting your search or filter" 
              : "Create your first plan to get started"}
          </p>
          {(!search && statusFilter === "all") && (
            <button
              onClick={() => setShowModal(true)}
              className="inline-flex items-center gap-2 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors"
            >
              <Plus size={16} />
              Create First Plan
            </button>
          )}
        </div>
      ) : (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredPlans.map((p) => (
            <div key={p._id} className="border border-gray-200 rounded-xl p-2 bg-white hover:shadow-lg transition-shadow">
              {/* Plan Header */}
              <div className="flex justify-between items-start mb-4">
                <div>
                  <h3 className="text-lg font-bold text-gray-800">{p.title}</h3>
                  <span className={`text-xs font-medium px-2 py-1 rounded mt-1 inline-block ${
                    p.plan_type === "admin_manual" ? "bg-indigo-100 text-indigo-800" :
                    "bg-gray-100 text-gray-800"
                  }`}>
                    {p.plan_type === "admin_manual" ? "ADMIN ONLY" : "PUBLIC"}
                    {p.amount === 0 && " · FREE"}
                  </span>
                </div>
                <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                  p.is_active
                    ? "bg-emerald-100 text-emerald-800 border border-emerald-200"
                    : "bg-red-100 text-red-800 border border-red-200"
                }`}>
                  {p.is_active ? "ACTIVE" : "INACTIVE"}
                </span>
              </div>

              {/* Price */}
              <div className="mb-4">
                <p className="text-3xl font-bold text-gray-800">
                  {p.amount === 0 ? <span className="text-emerald-600">FREE</span> : `₹${p.amount}`}
                  <span className="text-sm font-normal text-gray-500"> / {p.duration_days} days</span>
                </p>
              </div>

              {/* Description */}
              <p className="text-gray-600 mb-4">{p.description}</p>

              {/* Highlighting Feature */}
              {p.highlighting_feature && (
                <p className="mb-4 text-sm font-medium text-blue-600">
                  ⭐ {p.highlighting_feature}
                </p>
              )}

              {/* Features List */}
              {p.features && p.features.length > 0 && (
                <div className="mb-4">
                  <h4 className="text-sm font-medium text-gray-700 mb-2">Features:</h4>
                  <ul className="space-y-1">
                    {p.features.slice(0, 3).map((feature, idx) => (
                      <li key={idx} className="flex items-start text-sm text-gray-600">
                        <svg className="w-4 h-4 text-green-500 mr-2 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                        </svg>
                        {feature}
                      </li>
                    ))}
                    {p.features.length > 3 && (
                      <li className="text-sm text-gray-500">
                        +{p.features.length - 3} more features
                      </li>
                    )}
                  </ul>
                </div>
              )}

              {/* Subscriber Count (if available) */}
              {p.subscriber_count !== undefined && (
                <p className="text-sm text-gray-500 mb-4">
                  {p.subscriber_count} active subscribers
                </p>
              )}

              {/* Action Buttons */}
              <div className="flex gap-2 pt-4 border-t border-gray-100">
                <button
                  onClick={() => openEdit(p)}
                  className="flex-1 border border-gray-300 text-gray-700 px-3 py-2 rounded-lg hover:bg-gray-50 transition-colors flex items-center justify-center gap-2"
                >
                  <Edit size={16} /> Edit
                </button>

                <button
                  onClick={() => togglePlanStatus(p)}
                  className={`flex-1 px-3 py-2 rounded-lg flex items-center justify-center gap-2 ${
                    p.is_active
                      ? "bg-amber-50 text-amber-700 border border-amber-200 hover:bg-amber-100"
                      : "bg-emerald-50 text-emerald-700 border border-emerald-200 hover:bg-emerald-100"
                  } transition-colors`}
                >
                  {p.is_active ? "Deactivate" : "Activate"}
                </button>

                <button
                  onClick={() => deletePlan(p)}
                  className="flex-1 bg-red-50 text-red-600 px-3 py-2 rounded-lg hover:bg-red-100 transition-colors flex items-center justify-center gap-2 border border-red-200"
                >
                  <Trash2 size={16} /> Delete
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* ================= MODAL ================= */}
      {showModal && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-white w-full max-w-md rounded-xl shadow-xl max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-gray-200">
              <h3 className="text-xl font-bold text-gray-800">
                {editingPlan ? "Edit Plan" : "Create New Plan"}
              </h3>
            </div>

            <form onSubmit={savePlan} className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Plan Title *
                </label>
                <input
                  required
                  placeholder="e.g., Premium Plan"
                  value={form.title}
                  onChange={(e) => setForm({ ...form, title: e.target.value })}
                  className="w-full border border-gray-300 px-4 py-2.5 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Description *
                </label>
                <textarea
                  required
                  placeholder="Describe the plan features and benefits..."
                  value={form.description}
                  onChange={(e) => setForm({ ...form, description: e.target.value })}
                  className="w-full border border-gray-300 px-4 py-2.5 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all"
                  rows="3"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Amount (₹) *
                  </label>
                  <input
                    required
                    type="number"
                    placeholder="0 for a free plan"
                    value={form.amount}
                    onChange={(e) => setForm({ ...form, amount: e.target.value })}
                    min="0"
                    className="w-full border border-gray-300 px-4 py-2.5 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all"
                  />
                  <p className="text-xs text-gray-500 mt-1">Enter 0 to make this a free plan.</p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Duration (days) *
                  </label>
                  <input
                    required
                    type="number"
                    placeholder="30"
                    value={form.duration_days}
                    onChange={(e) => setForm({ ...form, duration_days: e.target.value })}
                    min="1"
                    className="w-full border border-gray-300 px-4 py-2.5 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Plan Type
                </label>
                <select
                  value={form.plan_type}
                  onChange={(e) => setForm({ ...form, plan_type: e.target.value })}
                  className="w-full border border-gray-300 px-4 py-2.5 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all"
                >
                  <option value="public">Public (shown to restaurants to self-select)</option>
                  <option value="admin_manual">Admin Only (used internally for manual/trial grants)</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Highlighting Feature *
                </label>
                <input
                  required
                  placeholder="e.g., 24/7 Support, Unlimited Storage"
                  value={form.highlighting_feature}
                  onChange={(e) => setForm({ ...form, highlighting_feature: e.target.value })}
                  className="w-full border border-gray-300 px-4 py-2.5 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all"
                />
              </div>

              <div>
                <div className="flex justify-between items-center mb-2">
                  <label className="block text-sm font-medium text-gray-700">
                    Features
                  </label>
                  <button
                    type="button"
                    onClick={addFeature}
                    className="text-sm text-blue-600 hover:text-blue-700 font-medium"
                  >
                    + Add Feature
                  </button>
                </div>
                <div className="space-y-2">
                  {features.map((feature, index) => (
                    <div key={index} className="flex gap-2">
                      <input
                        placeholder={`Feature ${index + 1}`}
                        value={feature}
                        onChange={(e) => updateFeature(index, e.target.value)}
                        className="flex-1 border border-gray-300 px-4 py-2 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      />
                      {features.length > 1 && (
                        <button
                          type="button"
                          onClick={() => removeFeature(index)}
                          className="px-3 py-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                        >
                          Remove
                        </button>
                      )}
                    </div>
                  ))}
                </div>
                <p className="text-xs text-gray-500 mt-2">
                  List key features of this plan. Empty features will be ignored.
                </p>
              </div>

              <div className="flex items-center gap-2 pt-2">
                <input
                  type="checkbox"
                  id="is_active"
                  checked={form.is_active}
                  onChange={(e) => setForm({ ...form, is_active: e.target.checked })}
                  className="h-4 w-4 text-blue-600 rounded focus:ring-blue-500"
                />
                <label htmlFor="is_active" className="text-sm font-medium text-gray-700">
                  Active Plan
                </label>
              </div>

              <div className="flex justify-end gap-3 pt-4 border-t border-gray-200">
                <button
                  type="button"
                  onClick={closeModal}
                  className="px-4 py-2.5 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                >
                  {editingPlan ? "Update Plan" : "Create Plan"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default PlanManagement;